<style lang="scss" scoped>
.page-wrap {
  padding: 8px 0 80px;
  font-family: 'Segoe UI', sans-serif;
}

.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
}

/* ── Header ── */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;

  .panel-title {
    font-size: 13px;
    font-weight: 700;
    color: #389ad4;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .btn-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 14px;
    background: #fff;
    color: #4a5568;
    border: 1.5px solid #dde2ec;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover { border-color: #389ad4; color: #389ad4; }
  }
}

/* ── Form grid ── */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 20px;
  padding: 20px 20px 4px;
  align-items: start;
}

/* ── Section cards ── */
.section-card {
  background: #f8fafd;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 14px;

  &:last-child { margin-bottom: 0; }

  .section-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #389ad4;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e8edf5;
  }
}

/* ── Field groups ── */
.field-group {
  margin-bottom: 13px;

  &:last-child { margin-bottom: 0; }

  label.field-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #7a8499;
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    gap: 4px;

    .req { color: #e53e3e; font-size: 12px; }
    .note { color: #a0aab8; font-size: 10px; font-weight: 400; text-transform: none; letter-spacing: 0; }
  }

  input[type="text"],
  input[type="number"],
  select,
  textarea {
    width: 100%;
    border: 1.5px solid #dde2ec;
    border-radius: 8px;
    font-size: 13px;
    color: #0d1b3e;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    background: #fff;
    box-sizing: border-box;

    &::placeholder { color: #b0bac9; }
    &:focus { border-color: #389ad4; box-shadow: 0 0 0 3px rgba(56,154,212,0.1); }
    &.has-error { border-color: #e53e3e; }
    &:disabled { background: #f5f7fa; color: #a0aab8; cursor: not-allowed; }
  }

  input[type="text"],
  input[type="number"],
  select { height: 38px; padding: 0 12px; }

  select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 28px;
  }

  textarea { padding: 8px 12px; resize: vertical; min-height: 80px; }

  .err-msg {
    font-size: 11px;
    color: #e53e3e;
    margin-top: 4px;
    display: block;
  }
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 13px;

  .field-group { margin-bottom: 0; }
}

.field-row-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  margin-bottom: 13px;

  .field-group { margin-bottom: 0; }
}

.field-row-5 {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 0;

  .field-group { margin-bottom: 0; }
}

/* ── Number input: hide spinners ── */
input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }

/* ── Additional rate inline toggle ── */
.addon-input {
  display: flex;
  align-items: center;
  gap: 8px;

  input { flex: 1; }
}

/* ── Toggle switch ── */
.switch-wrap {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
  flex-shrink: 0;

  input { opacity: 0; width: 0; height: 0; }

  .slider {
    position: absolute;
    inset: 0;
    background: #cbd5e0;
    border-radius: 22px;
    cursor: pointer;
    transition: background 0.2s;

    &::before {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      left: 3px;
      top: 3px;
      background: #fff;
      border-radius: 50%;
      transition: transform 0.2s;
    }
  }

  input:checked + .slider { background: #389ad4; }
  input:checked + .slider::before { transform: translateX(16px); }
}

/* ── Toggle item (label + switch in a row) ── */
.toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f3fa;

  &:last-child { border-bottom: none; padding-bottom: 0; }
  &:first-child { padding-top: 0; }

  .toggle-label {
    font-size: 13px;
    color: #2d3748;
    font-weight: 500;
  }
}

/* ── Image upload card ── */
.image-preview {
  width: 100%;
  height: 120px;
  border-radius: 8px;
  border: 1.5px dashed #dde2ec;
  background: #f8fafd;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 10px;

  img { max-width: 100%; max-height: 100%; object-fit: contain; }

  canvas { max-width: 100%; }

  .no-image {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: #a0aab8;
    font-size: 12px;

    i { font-size: 24px; opacity: 0.5; }
  }
}

.btn-upload {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  background: #eef2fb;
  color: #185FA5;
  border: 1.5px solid #c5d4f0;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;

  &:hover { background: #389ad4; color: #fff; border-color: #389ad4; }

  input[type="file"] { display: none; }
}

.err-img { font-size: 11px; color: #e53e3e; margin-top: 4px; display: block; }

/* ── QuickBooks spinner ── */
.qb-loader-wrap {
  position: relative;

  .qb-spinner {
    position: absolute;
    inset: 0;
    background: rgba(255,255,255,0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    z-index: 1;

    i { font-size: 16px; color: #389ad4; }
  }
}

/* ── Footer ── */
.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-top: 1px solid #f0f3fa;
  background: #fafbfd;

  .footer-actions { display: flex; gap: 10px; }

  .btn-back-footer {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 16px;
    background: #fff;
    color: #4a5568;
    border: 1.5px solid #dde2ec;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover { border-color: #389ad4; color: #389ad4; }
  }

  .btn-save {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 20px;
    background: #F58020;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover { opacity: 0.9; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }

  .btn-outline-save {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 20px;
    background: #fff;
    color: #F58020;
    border: 1.5px solid #F58020;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover { background: #F58020; color: #fff; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
}
</style>

<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <span class="panel-title">
          <i class="fa fa-concierge-bell"></i>
          {{ isEdit ? 'Edit Service' : 'New Service' }}
        </span>
        <button class="btn-back" @click="goBack()">
          <i class="fa fa-arrow-left"></i> Back
        </button>
      </div>

      <!-- Form Grid -->
      <div class="form-grid">

        <!-- ── LEFT COLUMN ── -->
        <div>

          <!-- Service Provider (admin only) -->
          <div class="section-card" v-if="currentUser.u.is_admin">
            <div class="section-title"><i class="fa fa-building"></i> Service Provider</div>
            <div class="field-group">
              <label class="field-label">
                Service Provider
                <span class="req" v-if="!isEdit">*</span>
                <span class="note" v-if="isEdit">(cannot be changed)</span>
              </label>
              <select
                v-model="service_provider"
                :disabled="isEdit"
                :class="{ 'has-error': errors['service_provider'] }"
              >
                <option value="">Please select a service provider</option>
                <option
                  v-for="(p, i) in getserviceProviders"
                  :key="i"
                  :value="p.id"
                >{{ p.name }}</option>
              </select>
              <span class="err-msg" v-if="errors['service_provider']">{{ errors['service_provider'][0] }}</span>
            </div>
          </div>

          <!-- Service Information -->
          <div class="section-card">
            <div class="section-title"><i class="fa fa-info-circle"></i> Service Information</div>

            <div class="field-row">
              <div class="field-group">
                <label class="field-label">Service Type <span class="req">*</span></label>
                <select v-model="service_code" :class="{ 'has-error': errors['service_code'] }">
                  <option value="">Please select a service type</option>
                  <option
                    v-for="(c, i) in getServiceCodes"
                    :key="i"
                    :value="c.id"
                  >{{ c.service_name }}</option>
                </select>
                <span class="err-msg" v-if="errors['service_code']">{{ errors['service_code'][0] }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">DAI <span style="font-weight:400; text-transform:none; letter-spacing:0;">(Destination Agency Identifier)</span></label>
                <input type="text" v-model="dai" :class="{ 'has-error': errors['dai'] }" />
                <span class="err-msg" v-if="errors['dai']">{{ errors['dai'][0] }}</span>
              </div>
            </div>

            <div class="field-group">
              <label class="field-label">Name <span class="req">*</span></label>
              <input type="text" v-model="service_name" :class="{ 'has-error': errors['name'] }" />
              <span class="err-msg" v-if="errors['name']">{{ errors['name'][0] }}</span>
            </div>

            <div class="field-group">
              <label class="field-label">Description <span class="req">*</span></label>
              <textarea
                v-model="service_description"
                rows="3"
                :class="{ 'has-error': errors['description'] }"
              ></textarea>
              <span class="err-msg" v-if="errors['description']">{{ errors['description'][0] }}</span>
            </div>

            <div class="field-row">
              <div class="field-group">
                <label class="field-label">Service Category</label>
                <select v-model="service_type">
                  <option value="none">None</option>
                  <option value="citizen">Enable Citizen</option>
                  <option value="DISCLOSURE">Disclosure</option>
                </select>
              </div>
              <div class="field-group" v-if="!isApostille">
                <label class="field-label">Is Drug Testing</label>
                <div style="padding-top:8px;">
                  <label class="switch-wrap">
                    <input type="checkbox" v-model="is_drug_testing" />
                    <span class="slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <div class="field-row">
              <div class="field-group">
                <label class="field-label">Rate Title <span class="req">*</span></label>
                <input type="text" v-model="rate_title" :class="{ 'has-error': errors['rate_title'] }" />
                <span class="err-msg" v-if="errors['rate_title']">{{ errors['rate_title'][0] }}</span>
              </div>
              <div class="field-group" v-if="has_additional_rate">
                <label class="field-label">Additional Rate Title <span class="req">*</span></label>
                <input type="text" v-model="additional_field_note" :class="{ 'has-error': errors['additional_field_note'] }" />
                <span class="err-msg" v-if="errors['additional_field_note']">{{ errors['additional_field_note'][0] }}</span>
              </div>
            </div>
          </div>

          <!-- Service Charge Detail -->
          <div class="section-card">
            <div class="section-title">
              <i class="fa fa-dollar-sign"></i>
              {{ isApostille ? 'Normal Service Charge Detail' : 'Service Charge Detail' }}
            </div>
            <div class="field-row-5">
              <div class="field-group">
                <label class="field-label">Default Rate <span class="req">*</span></label>
                <input type="number" v-model="default_rate" min="0" />
              </div>
              <div class="field-group">
                <label class="field-label">Default Qty <span class="req">*</span></label>
                <input type="number" v-model="default_qty" min="0" />
              </div>
              <div class="field-group" v-if="!isApostille">
                <label class="field-label">Additional Rate</label>
                <div class="addon-input">
                  <label class="switch-wrap" style="margin-top:2px;">
                    <input type="checkbox" v-model="has_additional_rate" />
                    <span class="slider"></span>
                  </label>
                  <input type="number" :disabled="!has_additional_rate" v-model="additional_rate" style="flex:1;" />
                </div>
              </div>
              <div class="field-group">
                <label class="field-label">Tax (%)</label>
                <input type="number" v-model="tax_percentag" min="0" />
              </div>
              <div class="field-group">
                <label class="field-label">Commission ($)</label>
                <input type="number" v-model="commission_percentage" min="0" />
              </div>
            </div>
          </div>

          <!-- Expedited Charge (Apostille only) -->
          <div class="section-card" v-if="isApostille">
            <div class="section-title"><i class="fa fa-bolt"></i> Expedited Charge Details</div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">Expedited Rate <span class="req">*</span></label>
                <input type="number" v-model="default_expedited_rate" min="0" />
              </div>
            </div>
          </div>

          <!-- Apostille Rate Notes -->
          <div class="section-card" v-if="isApostille">
            <div class="section-title"><i class="fa fa-sticky-note"></i> Apostille Rate Notes</div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">Regular Rate Note <span class="req">*</span></label>
                <textarea v-model="default_rate_note" rows="3"></textarea>
              </div>
              <div class="field-group">
                <label class="field-label">Expedited Rate Note <span class="req">*</span></label>
                <textarea v-model="expedited_rate_note" rows="3"></textarea>
              </div>
            </div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">Document Translation Rate Note <span class="req">*</span></label>
                <textarea v-model="doc_translation_rate_note" rows="3"></textarea>
              </div>
              <div class="field-group">
                <label class="field-label">Document Scanning Rate Note <span class="req">*</span></label>
                <textarea v-model="doc_scan_rate_note" rows="3"></textarea>
              </div>
            </div>
          </div>

          <!-- Shipping (Apostille only) -->
          <div class="section-card" v-if="isApostille">
            <div class="section-title"><i class="fa fa-shipping-fast"></i> Shipping Service Rate</div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">Shipping Company Name <span class="req">*</span></label>
                <input type="text" v-model="shipping_details.shipping_company_name" :class="{ 'has-error': errors['mailing_company_name'] }" />
              </div>
              <div class="field-group">
                <label class="field-label">Domestic Shipping Rate <span class="req">*</span></label>
                <input type="number" v-model="shipping_details.domestic_shipping_cost" :class="{ 'has-error': errors['domestic_shipping_rate'] }" />
              </div>
            </div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">International Shipping Rate <span class="req">*</span></label>
                <input type="number" v-model="shipping_details.international_shipping_cost" :class="{ 'has-error': errors['international_shipping_rate'] }" />
              </div>
              <div class="field-group">
                <label class="field-label">Eastern Europe Shipping Rate <span class="req">*</span></label>
                <input type="number" v-model="shipping_details.eastern_europe_shipping_cost" />
              </div>
            </div>

            <div class="section-title" style="margin-top:12px;"><i class="fa fa-plus-circle"></i> Optional Service Addons Rate</div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">Document Translation Rate (per page) <span class="req">*</span></label>
                <input type="number" v-model="doc_translation_rate" min="0" :class="{ 'has-error': errors['doc_translation_rate'] }" />
              </div>
              <div class="field-group">
                <label class="field-label">Scan of Completed Docs Rate (per doc) <span class="req">*</span></label>
                <input type="number" v-model="doc_scan_rate" min="0" :class="{ 'has-error': errors['doc_scan_rate'] }" />
              </div>
            </div>
          </div>

          <!-- QuickBooks -->
          <div class="section-card" v-if="currentUser.c.is_quickbooks_enabled == true">
            <div class="section-title"><i class="fa fa-book"></i> QuickBooks Mapping</div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">QuickBooks Account <span class="req">*</span></label>
                <div class="qb-loader-wrap">
                  <select v-model="quickbook_id" :class="{ 'has-error': errors['quickbook_id'] }">
                    <option value="">Please select QuickBooks Account</option>
                    <option
                      v-for="(q, i) in qb_account_list"
                      :key="i"
                      :value="q.id"
                    >{{ q.name }} ({{ q.type }})</option>
                  </select>
                  <div class="qb-spinner" v-if="qb_loading">
                    <i class="fa fa-spinner fa-spin"></i>
                  </div>
                </div>
                <span class="err-msg" v-if="errors['quickbook_id']">{{ errors['quickbook_id'][0] }}</span>
              </div>
            </div>
          </div>

          <!-- Book Now Note -->
          <div class="section-card">
            <div class="section-title"><i class="fa fa-sticky-note"></i> Book Now Note</div>
            <editor
              api-key="no-api-key"
              v-model="book_now_note"
              :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js"
              v-if="showBookNow"
            />
          </div>

        </div>

        <!-- ── RIGHT COLUMN ── -->
        <div>

          <!-- Image Upload -->
          <div class="section-card">
            <div class="section-title"><i class="fa fa-image"></i> Service Image</div>

            <div class="image-preview">
              <canvas
                id="imageCanvas"
                ref="imageCanvas"
                v-if="showImage"
                style="max-width:100%; max-height:115px;"
              ></canvas>
              <img
                v-else-if="image_place"
                :src="image_place"
                alt="Service Image"
              />
              <div class="no-image" v-else>
                <i class="fa fa-image"></i>
                No image
              </div>
            </div>

            <label class="btn-upload">
              <input type="file" id="imageLoader" @change="updateCanvasImage" hidden />
              <i class="fa fa-upload"></i> Upload Image
            </label>
            <span class="err-img" v-if="errors['image']">{{ errors['image'][0] }}</span>
          </div>

          <!-- Settings Toggles -->
          <div class="section-card">
            <div class="section-title"><i class="fa fa-cog"></i> Settings</div>

            <div class="toggle-item">
              <span class="toggle-label">Active</span>
              <label class="switch-wrap">
                <input type="checkbox" v-model="is_active" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="toggle-item">
              <span class="toggle-label">Featured</span>
              <label class="switch-wrap">
                <input type="checkbox" v-model="is_featured" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="toggle-item">
              <span class="toggle-label">Biometric Disclosure</span>
              <label class="switch-wrap">
                <input type="checkbox" v-model="enable_biometric_data_collection_disclosure" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="toggle-item">
              <span class="toggle-label">Show Capture Option</span>
              <label class="switch-wrap">
                <input type="checkbox" v-model="show_capture_method" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="toggle-item">
              <span class="toggle-label">Email Verification</span>
              <label class="switch-wrap">
                <input type="checkbox" v-model="email_verification" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="toggle-item">
              <span class="toggle-label">SMS Verification</span>
              <label class="switch-wrap">
                <input type="checkbox" v-model="sms_verification" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="toggle-item">
              <span class="toggle-label">Enable ORI Rate</span>
              <label class="switch-wrap">
                <input type="checkbox" v-model="enable_ori_rate" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="toggle-item">
              <span class="toggle-label">Disable PII</span>
              <label class="switch-wrap">
                <input type="checkbox" v-model="disable_pii" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="toggle-item">
              <span class="toggle-label">Service By Mail</span>
              <label class="switch-wrap">
                <input type="checkbox" v-model="is_service_by_mail" />
                <span class="slider"></span>
              </label>
            </div>
            <div class="toggle-item">
              <span class="toggle-label">Two Factor Auth</span>
              <label class="switch-wrap">
                <input type="checkbox" v-model="enable_two_factor" />
                <span class="slider"></span>
              </label>
            </div>
          </div>

        </div>
      </div>

      <!-- Footer -->
      <div class="form-footer">
        <button class="btn-back-footer" @click="goBack()">
          <i class="fa fa-arrow-left"></i> Back
        </button>
        <div class="footer-actions">
          <template v-if="!isEdit">
            <button
              class="btn-outline-save"
              @click="store('kt_save_and_add_service', 'SAA')"
              ref="kt_save_and_add_service"
            >
              Save &amp; Add Another
            </button>
            <button
              class="btn-save"
              @click="store('kt_save_service', 'S')"
              ref="kt_save_service"
            >
              <i class="fa fa-check"></i> Save
            </button>
          </template>
          <button
            v-if="isEdit"
            class="btn-save"
            @click="update('kt_update_service')"
            ref="kt_update_service"
          >
            <i class="fa fa-check"></i> Update
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Editor from "@tinymce/tinymce-vue";
import { mapGetters } from "vuex";
import {
  UPDATE_B_SERVICE,
  STORE_B_SERVICE,
  GET_B_SERVICE_DATA,
  FETCH_SERVICE_CODES,
  FETCH_QUICKBOOK_ACCOUNTS,
  GET_SERVICE_PROVIDER_LIST,
} from "@/core/services/store/actions.type";

export default {
  components: { editor: Editor },
  data() {
    return {
      isEdit: false,
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Services", route: "client.service" },
        { id: 3, title: "Create New Service", route: "" }
      ],
      editor: ClassicEditor,
      config: {
        license_key: 'gpl',
        height: 500,
        menubar: false,
        plugins: [
          "advlist autolink lists link image charmap print preview anchor",
          "searchreplace visualblocks code fullscreen",
          "insertdatetime media table paste code help wordcount"
        ],
        toolbar:
          "undo redo | formatselect | fontsizeselect | bold italic backcolor | " +
          "alignleft aligncenter alignright alignjustify | " +
          "bullist numlist outdent indent | removeformat | code | help",
        fontsize_formats: "8pt 9pt 10pt 11pt 12pt 14pt 18pt 24pt 30pt 36pt 48pt 60pt 72pt 96pt"
      },
      showImage: false,
      showBookNow: false,
      book_now_note: "",
      service_code: "",
      service_provider: "",
      rate_title: "",
      additional_field_note: "",
      service_name: "",
      dai: "",
      service_description: "",
      service_type: "none",
      image: "",
      image_place: "",
      is_active: true,
      default_rate: 0,
      default_qty: 1,
      additional_rate: 0,
      commission_percentage: 0,
      tax_percentag: 0,
      has_additional_rate: false,
      enable_two_factor: false,
      show_capture_method: false,
      email_verification: false,
      sms_verification: false,
      is_featured: false,
      is_drug_testing: 0,
      is_service_by_mail: false,
      enable_ori_rate: false,
      disable_pii: false,
      enable_biometric_data_collection_disclosure: false,
      isApostille: false,
      doc_translation_rate: 0,
      doc_scan_rate: 0,
      default_expedited_rate: 0,
      expedited_rate_note: '',
      default_rate_note: '',
      doc_translation_rate_note: '',
      doc_scan_rate_note: '',
      shipping_details: {
        shipping_company_name: "",
        domestic_shipping_cost: 0,
        international_shipping_cost: 0,
        eastern_europe_shipping_cost: 0,
      },
      quickbook_id: "",
      qb_loading: false,
      qb_account_list: [],
      errors: [],
      e: "",
      image_base64: "",
    };
  },
  computed: {
    ...mapGetters([
      "getserviceProviders",
      "currentUser",
      "getAspectheight",
      "getAspectwidth",
      "getCanvasheight",
      "getServiceCodes"
    ])
  },
  watch: {
    service_code() { this.checkApostille(); },
    getServiceCodes() { this.checkApostille(); }
  },
  mounted() {
    this.$store.dispatch(FETCH_SERVICE_CODES).then(() => {
      setTimeout(() => { this.showBookNow = true; }, 1500);
    });
  },
  created() {
    if (this.currentUser.u.is_admin) this.getServiceProvider();
    this.fetchQuickBookAccounts();
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.isEdit = this.$route.name === "client.service.edit";
    if (this.isEdit) {
      this.breadCrumbs[2].title = "";
      this.$store
        .dispatch(GET_B_SERVICE_DATA, this.$route.params.id)
        .then(data => {
          this.service_name = data.name;
          this.breadCrumbs[2].title = "Edit | " + data.name;
          this.service_description = data.description;
          this.service_type = data.service_type;
          this.is_active = data.is_active;
          this.service_code = data.service_code;
          this.image_place = data.image;
          this.default_rate = data.default_rate;
          this.additional_rate = data.additional_rate;
          this.tax_percentag = data.tax_percentage;
          this.commission_percentage = data.commission_percentage;
          this.has_additional_rate = data.has_additional_rate;
          this.enable_two_factor = data.enable_two_factor;
          this.default_qty = data.default_qty;
          this.sms_verification = data.sms_verification;
          this.email_verification = data.email_verification;
          this.rate_title = data.rate_title;
          this.additional_field_note = data.additional_field_note;
          this.book_now_note = data.book_now_note;
          this.show_capture_method = data.show_capture_method;
          this.enable_ori_rate = data.enable_ori_rate;
          this.disable_pii = data.disable_pii;
          this.dai = data.dai;
          this.is_drug_testing = data.is_drug_testing == null ? 0 : data.is_drug_testing;
          this.is_featured = data.is_featured == null ? 0 : data.is_featured;
          this.is_service_by_mail = data.is_service_by_mail;
          this.doc_translation_rate = data.doc_translation_rate;
          this.doc_scan_rate = data.doc_translation_rate;
          this.default_expedited_rate = data.default_expedited_rate;
          this.expedited_rate_note = data.expedited_rate_note;
          this.default_rate_note = data.default_rate_note;
          this.doc_translation_rate_note = data.doc_translation_rate_note;
          this.doc_scan_rate_note = data.doc_scan_rate_note;
          this.enable_biometric_data_collection_disclosure = data.enable_biometric_data_collection_disclosure;
          this.service_provider = data.client;
          this.shipping_details = data.shipping_details || {
            shipping_company_name: "",
            domestic_shipping_cost: 0,
            international_shipping_cost: 0,
            eastern_europe_shipping_cost: 0,
          };
          this.quickbook_id = data.quickbook_id || "";
        })
        .catch(() => {
          this.$toastr.e("Service detail not found!");
          this.$router.push({ name: "client.service" });
        });
    }
  },
  methods: {
    checkApostille() {
      const val = this.getServiceCodes.find(obj => obj.id == this.service_code);
      this.isApostille = val ? val.code === "apostille" : false;
    },
    goBack() {
      this.$router.push({
        name: "client.service",
        params: { client_slug: this.$route.params.client_slug }
      });
    },
    clearForm() {
      this.service_name = "";
      this.service_code = "";
      this.service_provider = "";
      this.service_description = "";
      this.service_type = "";
      this.image_base64 = "";
      this.image = "";
      this.image_place = "";
      this.is_active = true;
      this.default_rate = 0;
      this.additional_rate = 0;
      this.commission_percentage = 0;
      this.tax_percentag = 0;
      this.default_qty = 1;
      this.rate_title = "";
      this.additional_field_note = "";
      this.has_additional_rate = false;
      this.enable_ori_rate = false;
      this.disable_pii = false;
      this.show_capture_method = false;
      this.sms_verification = false;
      this.email_verification = false;
      this.enable_two_factor = false;
      this.dai = "";
      this.is_drug_testing = false;
      this.is_featured = false;
      this.is_service_by_mail = false;
      this.doc_translation_rate = 0;
      this.doc_scan_rate = 0;
      this.default_expedited_rate = 0;
      this.expedited_rate_note = '';
      this.default_rate_note = '';
      this.doc_translation_rate_note = '';
      this.doc_scan_rate_note = '';
      this.enable_biometric_data_collection_disclosure = false;
      this.shipping_details = {
        shipping_company_name: "",
        domestic_shipping_cost: 0,
        international_shipping_cost: 0,
        eastern_europe_shipping_cost: 0,
      };
      this.quickbook_id = "";
      this.showImage = false;
      this.errors = [];
    },
    loadingButton(ref) {
      const btn = this.$refs[ref];
      if (btn) { btn.disabled = true; btn.classList.add("spinner", "spinner-light", "spinner-right"); }
    },
    closeBtnLoad(ref) {
      const btn = this.$refs[ref];
      if (btn) { btn.disabled = false; btn.classList.remove("spinner", "spinner-light", "spinner-right"); }
    },
    update(ref) {
      this.loadingButton(ref);
      const _data = {
        name: this.service_name, description: this.service_description,
        service_type: this.service_type, image: this.image !== "" ? this.image : "none",
        is_active: this.is_active, service_code: this.service_code,
        service_provider: this.service_provider, default_rate: this.default_rate,
        additional_rate: this.additional_rate, enable_two_factor: this.enable_two_factor,
        commission_percentage: this.commission_percentage, tax_percentage: this.tax_percentag,
        has_additional_rate: this.has_additional_rate, default_qty: this.default_qty,
        rate_title: this.rate_title,
        additional_field_note: this.has_additional_rate ? this.additional_field_note : "",
        book_now_note: this.book_now_note, show_capture_method: this.show_capture_method,
        is_drug_testing: this.is_drug_testing, enable_ori_rate: this.enable_ori_rate,
        disable_pii: this.disable_pii, sms_verification: this.sms_verification,
        email_verification: this.email_verification, is_featured: this.is_featured,
        dai: this.dai, is_service_by_mail: this.is_service_by_mail,
        doc_translation_rate: this.doc_translation_rate, doc_scan_rate: this.doc_scan_rate,
        shipping_details: this.shipping_details, quickbook_id: this.quickbook_id,
        default_expedited_rate: this.default_expedited_rate,
        expedited_rate_note: this.expedited_rate_note, default_rate_note: this.default_rate_note,
        doc_translation_rate_note: this.doc_translation_rate_note,
        doc_scan_rate_note: this.doc_scan_rate_note,
        enable_biometric_data_collection_disclosure: this.enable_biometric_data_collection_disclosure,
      };
      this.$store
        .dispatch(UPDATE_B_SERVICE, { id: this.$route.params.id, data: _data })
        .then(data => { this.$toastr.s(data.msg); this.closeBtnLoad(ref); this.$router.push({ name: "client.service" }); })
        .catch(err => { this.errors = err; this.closeBtnLoad(ref); });
    },
    store(ref, saveType) {
      this.errors = [];
      this.loadingButton(ref);
      const _data = {
        name: this.service_name, description: this.service_description,
        service_type: this.service_type, image: this.image, is_active: this.is_active,
        service_code: this.service_code, service_provider: this.service_provider,
        default_rate: this.default_rate, additional_rate: this.additional_rate,
        commission_percentage: this.commission_percentage, tax_percentage: this.tax_percentag,
        enable_two_factor: this.enable_two_factor, has_additional_rate: this.has_additional_rate,
        default_qty: this.default_qty, enable_ori_rate: this.enable_ori_rate,
        disable_pii: this.disable_pii, rate_title: this.rate_title,
        additional_field_note: this.additional_field_note,
        show_capture_method: this.show_capture_method, book_now_note: this.book_now_note,
        is_drug_testing: this.is_drug_testing, email_verification: this.email_verification,
        sms_verification: this.sms_verification, is_featured: this.is_featured,
        dai: this.dai, is_service_by_mail: this.is_service_by_mail,
        doc_translation_rate: this.doc_translation_rate, doc_scan_rate: this.doc_scan_rate,
        shipping_details: this.shipping_details, quickbook_id: this.quickbook_id,
        default_expedited_rate: this.default_expedited_rate,
        expedited_rate_note: this.expedited_rate_note, default_rate_note: this.default_rate_note,
        doc_translation_rate_note: this.doc_translation_rate_note,
        doc_scan_rate_note: this.doc_scan_rate_note,
        enable_biometric_data_collection_disclosure: this.enable_biometric_data_collection_disclosure,
      };
      this.$store
        .dispatch(STORE_B_SERVICE, _data)
        .then(data => {
          this.$toastr.s(data.msg);
          this.closeBtnLoad(ref);
          if (saveType === "S") { this.$router.push({ name: "client.service" }); }
          else if (saveType === "SAA") { this.clearForm(); }
        })
        .catch(err => { this.errors = err; this.closeBtnLoad(ref); });
    },
    updateCanvasImage(e) {
      this.showImage = true;
      const reader = new FileReader();
      reader.onload = ev => {
        const img = new Image();
        img.onload = () => { this.drawCanvasImage(img); };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(e.target.files[0]);
    },
    drawCanvasImage(imageObj) {
      const canvas = this.$refs.imageCanvas;
      canvas.width = 120;
      canvas.height = (100 * imageObj.height) / imageObj.width;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(imageObj, 0, 0, 120, (100 * imageObj.height) / imageObj.width);
      this.image = canvas.toDataURL("image/png");
    },
    getServiceProvider() {
      this.$store
        .dispatch(GET_SERVICE_PROVIDER_LIST)
        .catch(err => { this.msg = err.error; });
    },
    fetchQuickBookAccounts() {
      this.qb_loading = true;
      this.$store.dispatch(FETCH_QUICKBOOK_ACCOUNTS)
        .then(data => { this.qb_account_list = data; })
        .catch(e => { this.$toastr.e(e.msg); })
        .finally(() => { this.qb_loading = false; });
    }
  }
};
</script>
